import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
    useMemo,
    forwardRef,
    useImperativeHandle,
} from 'react';
import {
    View,
    ScrollView,
    Image,
    Text,
    TouchableOpacity,
    Pressable,
    Animated,
    Dimensions,
    NativeSyntheticEvent,
    NativeScrollEvent,
    StyleSheet,
    Modal,
    ActivityIndicator,
} from 'react-native';
import type {
    DbSwiperProps,
    DbSwiperRef,
    SwiperItem,
    IndicatorConfig,
    ArrowConfig,
    AutoplayConfig,
    CardModeConfig,
    VideoConfig,
    PreviewConfig,
    MediaType,
} from './DbSwiper.types';
import { createStyles, getIndicatorPositionStyle, IndicatorPositionStyleKey, defaultColors } from './DbSwiper.styles';

// 尝试导入 react-native-video（可选依赖）
let Video: any = null;
try {
    Video = require('react-native-video').default;
} catch (e) {
    // react-native-video 未安装，视频功能将降级
}

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// 默认自动播放配置
const defaultAutoplayConfig: AutoplayConfig = {
    enabled: true,
    interval: 3000,
    pauseOnTouch: true,
    pauseOnInactive: true,
    direction: 'forward',
};

// 默认指示器配置
const defaultIndicatorConfig: IndicatorConfig = {
    type: 'dot',
    position: 'bottom',
    activeColor: defaultColors.indicatorActive,
    inactiveColor: defaultColors.indicatorInactive,
    dotSize: 6,
    activeDotWidth: 16,
    lineWidth: 16,
    lineHeight: 2,
    gap: 6,
    offset: 12,
};

// 默认箭头配置
const defaultArrowConfig: ArrowConfig = {
    show: true,
    position: 'inside',
    size: 32,
    color: '#FFFFFF',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 16,
};

// 默认卡片模式配置
const defaultCardModeConfig: CardModeConfig = {
    cardGap: 12,
    sideCardWidth: 40,
    sideCardScale: 0.9,
    sideCardOpacity: 0.7,
};

// 默认视频配置
const defaultVideoConfig: VideoConfig = {
    autoPlay: false,
    muted: false,
    loop: false,
    showControls: true,
    pauseOnSwipe: true,
    resetOnSwipe: false,
    resizeMode: 'contain',
};

// 默认预览配置
const defaultPreviewConfig: PreviewConfig = {
    enabled: true,
    showIndicator: true,
    indicatorType: 'fraction',
    showCloseButton: true,
    showSaveButton: false,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
};

// 获取媒体类型
const getMediaType = (item: SwiperItem): MediaType => {
    if (item.type) return item.type;
    if (item.video) return 'video';
    return 'image';
};

// 视频播放器组件
interface VideoPlayerProps {
    item: SwiperItem;
    index: number;
    isActive: boolean;
    videoConfig: VideoConfig;
    containerWidth: number;
    height: number;
    videoRef: React.RefObject<any>;
    onVideoStateChange?: (playing: boolean) => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
    item,
    index,
    isActive,
    videoConfig,
    containerWidth,
    height,
    videoRef,
    onVideoStateChange,
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showControls, setShowControls] = useState(true);
    
    // 当不是活动项时暂停视频
    useEffect(() => {
        if (!isActive && videoConfig.pauseOnSwipe && isPlaying) {
            setIsPlaying(false);
            onVideoStateChange?.(false);
            if (videoConfig.resetOnSwipe && videoRef.current) {
                videoRef.current.seek?.(0);
            }
        }
    }, [isActive, videoConfig.pauseOnSwipe, videoConfig.resetOnSwipe, isPlaying]);
    
    // 自动播放
    useEffect(() => {
        if (isActive && videoConfig.autoPlay && !isPlaying) {
            setIsPlaying(true);
            onVideoStateChange?.(true);
        }
    }, [isActive, videoConfig.autoPlay]);
    
    const handlePlayPause = () => {
        const newState = !isPlaying;
        setIsPlaying(newState);
        onVideoStateChange?.(newState);
        if (newState) {
            videoConfig.onPlay?.(item, index);
        } else {
            videoConfig.onPause?.(item, index);
        }
    };
    
    const handleLoad = () => {
        setIsLoading(false);
    };
    
    const handleEnd = () => {
        setIsPlaying(false);
        onVideoStateChange?.(false);
        videoConfig.onEnd?.(item, index);
    };
    
    const handleError = (error: any) => {
        setIsLoading(false);
        videoConfig.onError?.(item, index, error);
    };
    
    // 如果 Video 组件不可用，显示封面图和提示
    if (!Video) {
        const posterSource = item.videoPoster 
            ? (typeof item.videoPoster === 'string' ? { uri: item.videoPoster } : item.videoPoster)
            : (typeof item.image === 'string' ? { uri: item.image } : item.image);
        
        return (
            <View style={[videoStyles.container, { width: containerWidth, height }]}>
                {posterSource && (
                    <Image
                        source={posterSource}
                        style={videoStyles.poster}
                        resizeMode="cover"
                    />
                )}
                <View style={videoStyles.overlay}>
                    <View style={videoStyles.playButton}>
                        <Text style={videoStyles.playIcon}>▶</Text>
                    </View>
                    <Text style={videoStyles.videoHint}>需要安装 react-native-video</Text>
                </View>
                {item.videoDuration && (
                    <View style={videoStyles.durationBadge}>
                        <Text style={videoStyles.durationText}>
                            {Math.floor(item.videoDuration / 60)}:{(item.videoDuration % 60).toString().padStart(2, '0')}
                        </Text>
                    </View>
                )}
            </View>
        );
    }
    
    return (
        <Pressable 
            style={[videoStyles.container, { width: containerWidth, height }, videoConfig.containerStyle]}
            onPress={() => setShowControls(prev => !prev)}
        >
            <Video
                ref={videoRef}
                source={{ uri: item.video }}
                style={videoStyles.video}
                paused={!isPlaying}
                muted={videoConfig.muted}
                repeat={videoConfig.loop}
                resizeMode={videoConfig.resizeMode}
                onLoad={handleLoad}
                onEnd={handleEnd}
                onError={handleError}
                poster={typeof item.videoPoster === 'string' ? item.videoPoster : undefined}
                posterResizeMode="cover"
            />
            
            {/* 加载指示器 */}
            {isLoading && (
                <View style={videoStyles.loadingOverlay}>
                    <ActivityIndicator size="large" color="#fff" />
                </View>
            )}
            
            {/* 播放/暂停控制 */}
            {videoConfig.showControls && showControls && !isLoading && (
                <Pressable style={videoStyles.controlsOverlay} onPress={handlePlayPause}>
                    <View style={videoStyles.playButton}>
                        {videoConfig.playIcon || videoConfig.pauseIcon ? (
                            isPlaying ? videoConfig.pauseIcon : videoConfig.playIcon
                        ) : (
                            <Text style={videoStyles.playIcon}>{isPlaying ? '⏸' : '▶'}</Text>
                        )}
                    </View>
                </Pressable>
            )}
            
            {/* 视频时长 */}
            {item.videoDuration && !isPlaying && (
                <View style={videoStyles.durationBadge}>
                    <Text style={videoStyles.durationText}>
                        {Math.floor(item.videoDuration / 60)}:{(item.videoDuration % 60).toString().padStart(2, '0')}
                    </Text>
                </View>
            )}
        </Pressable>
    );
};

const videoStyles = StyleSheet.create({
    container: {
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#000',
    },
    video: {
        width: '100%',
        height: '100%',
    },
    poster: {
        width: '100%',
        height: '100%',
        position: 'absolute',
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.3)',
    },
    loadingOverlay: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
    },
    controlsOverlay: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'center',
        alignItems: 'center',
    },
    playButton: {
        width: 60,
        height: 60,
        borderRadius: 30,
        backgroundColor: 'rgba(0,0,0,0.6)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    playIcon: {
        fontSize: 24,
        color: '#fff',
        marginLeft: 4,
    },
    videoHint: {
        color: '#fff',
        fontSize: 12,
        marginTop: 12,
    },
    durationBadge: {
        position: 'absolute',
        right: 8,
        bottom: 8,
        backgroundColor: 'rgba(0,0,0,0.7)',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
    },
    durationText: {
        color: '#fff',
        fontSize: 12,
    },
});

// 图片预览组件
interface ImagePreviewModalProps {
    visible: boolean;
    images: SwiperItem[];
    initialIndex: number;
    previewConfig: PreviewConfig;
    onClose: () => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
    visible,
    images,
    initialIndex,
    previewConfig,
    onClose,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const scrollViewRef = useRef<ScrollView>(null);
    
    useEffect(() => {
        if (visible && scrollViewRef.current) {
            setTimeout(() => {
                scrollViewRef.current?.scrollTo({ x: initialIndex * SCREEN_WIDTH, animated: false });
            }, 50);
        }
        setCurrentIndex(initialIndex);
    }, [visible, initialIndex]);
    
    const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const index = Math.round(event.nativeEvent.contentOffset.x / SCREEN_WIDTH);
        if (index !== currentIndex && index >= 0 && index < images.length) {
            setCurrentIndex(index);
        }
    };
    
    // 只预览图片类型
    const imageItems = images.filter(item => getMediaType(item) === 'image');
    
    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={[previewStyles.container, { backgroundColor: previewConfig.backgroundColor }]}>
                {/* 关闭按钮 */}
                {previewConfig.showCloseButton && (
                    <Pressable style={previewStyles.closeButton} onPress={onClose}>
                        <Text style={previewStyles.closeIcon}>✕</Text>
                    </Pressable>
                )}
                
                {/* 图片列表 */}
                <ScrollView
                    ref={scrollViewRef}
                    horizontal
                    pagingEnabled
                    showsHorizontalScrollIndicator={false}
                    onMomentumScrollEnd={handleScroll}
                    style={previewStyles.scrollView}
                >
                    {imageItems.map((item, index) => {
                        const imageSource = typeof item.image === 'string' 
                            ? { uri: item.image } 
                            : item.image;
                        return (
                            <Pressable
                                key={item.id}
                                style={previewStyles.imageContainer}
                                onPress={onClose}
                                onLongPress={() => previewConfig.onLongPress?.(item, index)}
                            >
                                <Image
                                    source={imageSource!}
                                    style={previewStyles.image}
                                    resizeMode="contain"
                                />
                            </Pressable>
                        );
                    })}
                </ScrollView>
                
                {/* 指示器 */}
                {previewConfig.showIndicator && imageItems.length > 1 && (
                    <View style={previewStyles.indicatorContainer}>
                        {previewConfig.indicatorType === 'fraction' ? (
                            <Text style={previewStyles.fractionText}>
                                {currentIndex + 1} / {imageItems.length}
                            </Text>
                        ) : (
                            <View style={previewStyles.dotsContainer}>
                                {imageItems.map((_, index) => (
                                    <View
                                        key={index}
                                        style={[
                                            previewStyles.dot,
                                            index === currentIndex && previewStyles.dotActive,
                                        ]}
                                    />
                                ))}
                            </View>
                        )}
                    </View>
                )}
                
                {/* 保存按钮 */}
                {previewConfig.showSaveButton && (
                    <Pressable 
                        style={previewStyles.saveButton}
                        onPress={() => previewConfig.onSave?.(imageItems[currentIndex], currentIndex)}
                    >
                        <Text style={previewStyles.saveText}>保存</Text>
                    </Pressable>
                )}
            </View>
        </Modal>
    );
};

const previewStyles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.95)',
    },
    closeButton: {
        position: 'absolute',
        top: 50,
        right: 20,
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: 'rgba(255,255,255,0.2)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
    },
    closeIcon: {
        fontSize: 18,
        color: '#fff',
    },
    scrollView: {
        flex: 1,
    },
    imageContainer: {
        width: SCREEN_WIDTH,
        height: SCREEN_HEIGHT,
        justifyContent: 'center',
        alignItems: 'center',
    },
    image: {
        width: SCREEN_WIDTH,
        height: SCREEN_HEIGHT * 0.8,
    },
    indicatorContainer: {
        position: 'absolute',
        bottom: 60,
        left: 0,
        right: 0,
        alignItems: 'center',
    },
    fractionText: {
        color: '#fff',
        fontSize: 16,
    },
    dotsContainer: {
        flexDirection: 'row',
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: 'rgba(255,255,255,0.5)',
        marginHorizontal: 4,
    },
    dotActive: {
        backgroundColor: '#fff',
    },
    saveButton: {
        position: 'absolute',
        bottom: 60,
        right: 20,
        paddingHorizontal: 16,
        paddingVertical: 8,
        backgroundColor: 'rgba(255,255,255,0.2)',
        borderRadius: 16,
    },
    saveText: {
        color: '#fff',
        fontSize: 14,
    },
});

const DbSwiper = forwardRef<DbSwiperRef, DbSwiperProps>((props, ref) => {
    const {
        data,
        width = SCREEN_WIDTH,
        height = 200,
        borderRadius = 0,
        style,
        mode = 'default',
        cardModeConfig: cardModeConfigProp,
        vertical = false,
        autoplay = false,
        loop = true,
        initialIndex = 0,
        swipeable = true,
        indicator = true,
        arrows = false,
        duration = 300,
        animated = true,
        // 视频配置
        videoConfig: videoConfigProp,
        renderVideo: customRenderVideo,
        // 预览配置
        previewOnPress = false,
        // 内容渲染
        renderItem: customRenderItem,
        renderImage: customRenderImage,
        renderTitle: customRenderTitle,
        renderIndicator: customRenderIndicator,
        resizeMode = 'cover',
        placeholder,
        imageStyle,
        showTitle = false,
        titlePosition = 'inside',
        titleStyle,
        subtitleStyle,
        titleContainerStyle,
        onPress,
        onLongPress,
        onChange,
        onScrollStart,
        onScrollEnd,
        lazyLoad = false,
        preloadCount = 1,
        testID,
    } = props;

    // 计算实际宽度
    const containerWidth = typeof width === 'number' ? width : SCREEN_WIDTH;
    
    // 样式
    const styles = useMemo(() => createStyles(containerWidth, height), [containerWidth, height]);
    
    // 合并配置
    const autoplayConfig = useMemo<AutoplayConfig>(() => {
        if (typeof autoplay === 'boolean') {
            return { ...defaultAutoplayConfig, enabled: autoplay };
        }
        return { ...defaultAutoplayConfig, ...autoplay };
    }, [autoplay]);

    const indicatorConfig = useMemo<IndicatorConfig>(() => {
        if (typeof indicator === 'boolean') {
            return { ...defaultIndicatorConfig, type: indicator ? 'dot' : 'none' };
        }
        return { ...defaultIndicatorConfig, ...indicator };
    }, [indicator]);

    const arrowConfig = useMemo<ArrowConfig>(() => {
        if (typeof arrows === 'boolean') {
            return { ...defaultArrowConfig, show: arrows };
        }
        return { ...defaultArrowConfig, ...arrows };
    }, [arrows]);

    const cardModeConfig = useMemo<CardModeConfig>(() => {
        return { ...defaultCardModeConfig, ...cardModeConfigProp };
    }, [cardModeConfigProp]);

    const videoConfig = useMemo<VideoConfig>(() => {
        return { ...defaultVideoConfig, ...videoConfigProp };
    }, [videoConfigProp]);

    const previewConfig = useMemo<PreviewConfig>(() => {
        if (typeof previewOnPress === 'boolean') {
            return { ...defaultPreviewConfig, enabled: previewOnPress };
        }
        return { ...defaultPreviewConfig, ...previewOnPress };
    }, [previewOnPress]);

    // 状态
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isScrolling, setIsScrolling] = useState(false);
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([initialIndex]));
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewInitialIndex, setPreviewInitialIndex] = useState(0);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    
    // Refs
    const scrollViewRef = useRef<ScrollView>(null);
    const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isUserInteractingRef = useRef(false);
    const indicatorAnimRef = useRef(new Animated.Value(0)).current;
    const videoRefs = useRef<Map<number, React.RefObject<any>>>(new Map());
    
    // 为每个视频项创建 ref
    const getVideoRef = useCallback((index: number) => {
        if (!videoRefs.current.has(index)) {
            videoRefs.current.set(index, React.createRef());
        }
        return videoRefs.current.get(index)!;
    }, []);
    
    // 数据处理（循环模式需要前后各添加一项）
    const processedData = useMemo(() => {
        if (!loop || data.length <= 1) return data;
        return [data[data.length - 1], ...data, data[0]];
    }, [data, loop]);

    // 滚动到指定索引
    const scrollToIndex = useCallback((index: number, animatedScroll: boolean = true) => {
        if (!scrollViewRef.current) return;
        
        const targetIndex = loop ? index + 1 : index;
        const offset = vertical 
            ? targetIndex * height 
            : targetIndex * containerWidth;
        
        scrollViewRef.current.scrollTo({
            x: vertical ? 0 : offset,
            y: vertical ? offset : 0,
            animated: animatedScroll && animated,
        });
    }, [loop, vertical, height, containerWidth, animated]);

    // 停止所有视频
    const stopAllVideos = useCallback(() => {
        videoRefs.current.forEach((ref) => {
            if (ref.current?.seek) {
                ref.current.seek(0);
            }
        });
        setIsVideoPlaying(false);
    }, []);

    // 暂停当前视频
    const pauseVideo = useCallback(() => {
        setIsVideoPlaying(false);
    }, []);

    // 播放当前视频
    const playVideo = useCallback(() => {
        const currentItem = data[currentIndex];
        if (getMediaType(currentItem) === 'video') {
            setIsVideoPlaying(true);
        }
    }, [data, currentIndex]);

    // 打开预览
    const openPreview = useCallback((index?: number) => {
        setPreviewInitialIndex(index ?? currentIndex);
        setPreviewVisible(true);
    }, [currentIndex]);

    // 关闭预览
    const closePreview = useCallback(() => {
        setPreviewVisible(false);
    }, []);

    // 跳转到指定索引
    const goTo = useCallback((index: number, animatedScroll: boolean = true) => {
        const clampedIndex = Math.max(0, Math.min(index, data.length - 1));
        
        // 切换时暂停视频
        if (videoConfig.pauseOnSwipe) {
            pauseVideo();
        }
        
        scrollToIndex(clampedIndex, animatedScroll);
        setCurrentIndex(clampedIndex);
        onChange?.(clampedIndex, data[clampedIndex]);
    }, [data, scrollToIndex, onChange, videoConfig.pauseOnSwipe, pauseVideo]);

    // 下一页
    const next = useCallback((animatedScroll: boolean = true) => {
        const nextIndex = currentIndex >= data.length - 1 ? (loop ? 0 : currentIndex) : currentIndex + 1;
        goTo(nextIndex, animatedScroll);
    }, [currentIndex, data.length, loop, goTo]);

    // 上一页
    const prev = useCallback((animatedScroll: boolean = true) => {
        const prevIndex = currentIndex <= 0 ? (loop ? data.length - 1 : 0) : currentIndex - 1;
        goTo(prevIndex, animatedScroll);
    }, [currentIndex, data.length, loop, goTo]);

    // 停止自动播放
    const stopAutoplay = useCallback(() => {
        if (autoplayTimerRef.current) {
            clearInterval(autoplayTimerRef.current);
            autoplayTimerRef.current = null;
        }
    }, []);

    // 开始自动播放
    const startAutoplay = useCallback(() => {
        // 视频播放时不自动切换
        if (!autoplayConfig.enabled || data.length <= 1 || isVideoPlaying) return;
        
        stopAutoplay();
        
        autoplayTimerRef.current = setInterval(() => {
            if (isUserInteractingRef.current && autoplayConfig.pauseOnTouch) return;
            if (isVideoPlaying) return; // 视频播放中不自动切换
            
            if (autoplayConfig.direction === 'forward') {
                next(true);
            } else {
                prev(true);
            }
        }, autoplayConfig.interval);
    }, [autoplayConfig, data.length, stopAutoplay, next, prev, isVideoPlaying]);

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
        goTo,
        next,
        prev,
        startAutoplay,
        stopAutoplay,
        getCurrentIndex: () => currentIndex,
        playVideo,
        pauseVideo,
        stopAllVideos,
        openPreview,
        closePreview,
    }), [goTo, next, prev, startAutoplay, stopAutoplay, currentIndex, playVideo, pauseVideo, stopAllVideos, openPreview, closePreview]);

    // 初始化滚动位置
    useEffect(() => {
        if (loop && data.length > 1) {
            setTimeout(() => {
                scrollToIndex(initialIndex, false);
            }, 50);
        }
    }, []);

    // 自动播放控制
    useEffect(() => {
        if (autoplayConfig.enabled && !isVideoPlaying) {
            startAutoplay();
        } else {
            stopAutoplay();
        }
        return () => stopAutoplay();
    }, [autoplayConfig.enabled, startAutoplay, stopAutoplay, isVideoPlaying]);

    // 懒加载预加载
    useEffect(() => {
        if (lazyLoad) {
            const toLoad = new Set(loadedImages);
            for (let i = -preloadCount; i <= preloadCount; i++) {
                const idx = currentIndex + i;
                if (idx >= 0 && idx < data.length) {
                    toLoad.add(idx);
                }
            }
            if (toLoad.size !== loadedImages.size) {
                setLoadedImages(toLoad);
            }
        }
    }, [currentIndex, lazyLoad, preloadCount, data.length, loadedImages]);

    // 滚动事件处理
    const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { contentOffset } = event.nativeEvent;
        const offset = vertical ? contentOffset.y : contentOffset.x;
        const itemSize = vertical ? height : containerWidth;
        const rawIndex = offset / itemSize;
        
        const realIndex = loop ? rawIndex - 1 : rawIndex;
        indicatorAnimRef.setValue(realIndex);
    }, [vertical, height, containerWidth, loop, indicatorAnimRef]);

    const handleScrollBegin = useCallback(() => {
        setIsScrolling(true);
        isUserInteractingRef.current = true;
        onScrollStart?.();
        
        // 滑动时暂停视频
        if (videoConfig.pauseOnSwipe) {
            pauseVideo();
        }
    }, [onScrollStart, videoConfig.pauseOnSwipe, pauseVideo]);

    const handleScrollEnd = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { contentOffset } = event.nativeEvent;
        const offset = vertical ? contentOffset.y : contentOffset.x;
        const itemSize = vertical ? height : containerWidth;
        let newIndex = Math.round(offset / itemSize);
        
        // 循环模式边界处理
        if (loop && data.length > 1) {
            if (newIndex === 0) {
                setTimeout(() => {
                    scrollViewRef.current?.scrollTo({
                        x: vertical ? 0 : data.length * containerWidth,
                        y: vertical ? data.length * height : 0,
                        animated: false,
                    });
                }, 10);
                newIndex = data.length - 1;
            } else if (newIndex === processedData.length - 1) {
                setTimeout(() => {
                    scrollViewRef.current?.scrollTo({
                        x: vertical ? 0 : containerWidth,
                        y: vertical ? height : 0,
                        animated: false,
                    });
                }, 10);
                newIndex = 0;
            } else {
                newIndex = newIndex - 1;
            }
        }
        
        newIndex = Math.max(0, Math.min(newIndex, data.length - 1));
        
        if (newIndex !== currentIndex) {
            setCurrentIndex(newIndex);
            onChange?.(newIndex, data[newIndex]);
        }
        
        setIsScrolling(false);
        isUserInteractingRef.current = false;
        onScrollEnd?.(newIndex);
    }, [vertical, height, containerWidth, loop, data, processedData.length, currentIndex, onChange, onScrollEnd]);

    const handleTouchStart = useCallback(() => {
        isUserInteractingRef.current = true;
        if (autoplayConfig.pauseOnTouch) {
            stopAutoplay();
        }
    }, [autoplayConfig.pauseOnTouch, stopAutoplay]);

    const handleTouchEnd = useCallback(() => {
        isUserInteractingRef.current = false;
        if (autoplayConfig.enabled && !isVideoPlaying) {
            startAutoplay();
        }
    }, [autoplayConfig.enabled, startAutoplay, isVideoPlaying]);

    // 处理点击事件
    const handleItemPress = useCallback((item: SwiperItem, realIndex: number) => {
        const mediaType = getMediaType(item);
        
        // 如果是图片且启用了预览
        if (mediaType === 'image' && previewConfig.enabled) {
            openPreview(realIndex);
            return;
        }
        
        // 调用自定义点击回调
        onPress?.(item, realIndex);
    }, [previewConfig.enabled, openPreview, onPress]);

    // 渲染图片
    const renderImage = useCallback((item: SwiperItem, index: number) => {
        if (customRenderImage) {
            return customRenderImage(item, index);
        }
        
        // 懒加载检查
        if (lazyLoad && !loadedImages.has(index)) {
            return (
                <View style={styles.lazyPlaceholder}>
                    {placeholder || <Text style={styles.placeholderText}>加载中...</Text>}
                </View>
            );
        }
        
        if (!item.image) {
            return placeholder || (
                <View style={styles.placeholder}>
                    <Text style={styles.placeholderText}>暂无图片</Text>
                </View>
            );
        }
        
        const imageSource = typeof item.image === 'string' 
            ? { uri: item.image } 
            : item.image;
        
        return (
            <Image
                source={imageSource}
                style={[styles.image, imageStyle]}
                resizeMode={resizeMode}
            />
        );
    }, [customRenderImage, lazyLoad, loadedImages, placeholder, styles, imageStyle, resizeMode]);

    // 渲染视频
    const renderVideoItem = useCallback((item: SwiperItem, index: number, isActive: boolean) => {
        const videoRef = getVideoRef(index);
        
        if (customRenderVideo) {
            return customRenderVideo(item, index, isActive, videoRef);
        }
        
        return (
            <VideoPlayer
                item={item}
                index={index}
                isActive={isActive}
                videoConfig={videoConfig}
                containerWidth={containerWidth}
                height={height}
                videoRef={videoRef}
                onVideoStateChange={setIsVideoPlaying}
            />
        );
    }, [customRenderVideo, videoConfig, containerWidth, height, getVideoRef]);

    // 渲染标题
    const renderTitle = useCallback((item: SwiperItem, index: number) => {
        if (customRenderTitle) {
            return customRenderTitle(item, index);
        }
        
        if (!showTitle || (!item.title && !item.subtitle)) {
            return null;
        }
        
        const isOutside = titlePosition === 'outside';
        
        return (
            <View style={[
                styles.titleContainer,
                isOutside && styles.titleContainerOutside,
                titleContainerStyle,
            ]}>
                {item.title && (
                    <Text 
                        style={[
                            styles.title, 
                            isOutside && styles.titleOutside,
                            titleStyle,
                        ]} 
                        numberOfLines={1}
                    >
                        {item.title}
                    </Text>
                )}
                {item.subtitle && (
                    <Text 
                        style={[
                            styles.subtitle, 
                            isOutside && styles.subtitleOutside,
                            subtitleStyle,
                        ]} 
                        numberOfLines={2}
                    >
                        {item.subtitle}
                    </Text>
                )}
            </View>
        );
    }, [customRenderTitle, showTitle, titlePosition, styles, titleContainerStyle, titleStyle, subtitleStyle]);

    // 渲染轮播项
    const renderSlideItem = useCallback((item: SwiperItem, index: number) => {
        const realIndex = loop ? index - 1 : index;
        const isActive = realIndex === currentIndex;
        const mediaType = getMediaType(item);
        
        // 自定义渲染
        if (customRenderItem) {
            return (
                <View key={item.id} style={[styles.slideItem, { borderRadius }]}>
                    {customRenderItem(item, realIndex, isActive)}
                </View>
            );
        }
        
        // 卡片模式样式
        const cardStyle = mode === 'card' ? [
            styles.cardItem,
            !isActive && styles.cardItemSide,
            {
                marginHorizontal: cardModeConfig.cardGap! / 2,
                transform: isActive ? [] : [{ scale: cardModeConfig.sideCardScale! }],
                opacity: isActive ? 1 : cardModeConfig.sideCardOpacity!,
            },
        ] : [];
        
        // 渲染媒体内容
        const renderMediaContent = () => {
            if (mediaType === 'video') {
                return renderVideoItem(item, realIndex, isActive);
            }
            return renderImage(item, realIndex);
        };
        
        return (
            <TouchableOpacity
                key={item.id}
                activeOpacity={0.95}
                onPress={() => handleItemPress(item, realIndex)}
                onLongPress={() => onLongPress?.(item, realIndex)}
                style={[styles.slideItem, { borderRadius }, ...cardStyle]}
            >
                {renderMediaContent()}
                {titlePosition === 'inside' && renderTitle(item, realIndex)}
            </TouchableOpacity>
        );
    }, [loop, currentIndex, customRenderItem, styles, borderRadius, mode, cardModeConfig, handleItemPress, onLongPress, renderImage, renderVideoItem, titlePosition, renderTitle]);

    // 渲染指示器
    const renderIndicatorContent = useCallback(() => {
        if (indicatorConfig.type === 'none') return null;
        
        const positionStyleKey: IndicatorPositionStyleKey = getIndicatorPositionStyle(indicatorConfig.position!);
        const isVerticalIndicator = indicatorConfig.position === 'left' || indicatorConfig.position === 'right';
        
        if (customRenderIndicator) {
            return (
                <View style={[
                    styles.indicatorContainer,
                    styles[positionStyleKey],
                    { bottom: indicatorConfig.offset },
                    indicatorConfig.style,
                ]}>
                    {customRenderIndicator(currentIndex, data.length)}
                </View>
            );
        }
        
        return (
            <View style={[
                styles.indicatorContainer,
                styles[positionStyleKey],
                indicatorConfig.style,
            ]}>
                {indicatorConfig.type === 'dot' && data.map((_, index) => {
                    const isActiveIndicator = index === currentIndex;
                    return (
                        <Animated.View
                            key={index}
                            style={[
                                styles.dot,
                                isVerticalIndicator && styles.dotVertical,
                                {
                                    width: indicatorConfig.dotSize,
                                    height: indicatorConfig.dotSize,
                                    borderRadius: indicatorConfig.dotSize! / 2,
                                    backgroundColor: indicatorConfig.inactiveColor,
                                    marginHorizontal: isVerticalIndicator ? 0 : indicatorConfig.gap! / 2,
                                    marginVertical: isVerticalIndicator ? indicatorConfig.gap! / 2 : 0,
                                },
                                isActiveIndicator && {
                                    width: indicatorConfig.activeDotWidth,
                                    backgroundColor: indicatorConfig.activeColor,
                                },
                            ]}
                        />
                    );
                })}
                
                {indicatorConfig.type === 'line' && data.map((_, index) => {
                    const isActiveIndicator = index === currentIndex;
                    return (
                        <View
                            key={index}
                            style={[
                                styles.line,
                                {
                                    width: indicatorConfig.lineWidth,
                                    height: indicatorConfig.lineHeight,
                                    backgroundColor: isActiveIndicator 
                                        ? indicatorConfig.activeColor 
                                        : indicatorConfig.inactiveColor,
                                    marginHorizontal: indicatorConfig.gap! / 2,
                                },
                            ]}
                        />
                    );
                })}
                
                {indicatorConfig.type === 'number' && (
                    <View style={styles.numberIndicator}>
                        <Text style={styles.numberText}>
                            {currentIndex + 1} / {data.length}
                        </Text>
                    </View>
                )}
                
                {indicatorConfig.type === 'progress' && (
                    <View style={styles.progressContainer}>
                        <Animated.View
                            style={[
                                styles.progressBar,
                                {
                                    width: `${((currentIndex + 1) / data.length) * 100}%`,
                                    backgroundColor: indicatorConfig.activeColor,
                                },
                            ]}
                        />
                    </View>
                )}
            </View>
        );
    }, [indicatorConfig, customRenderIndicator, currentIndex, data, styles]);

    // 渲染箭头
    const renderArrows = useCallback(() => {
        if (!arrowConfig.show || data.length <= 1) return null;
        
        const ArrowButton = ({ direction }: { direction: 'left' | 'right' }) => {
            const isLeft = direction === 'left';
            const handlePress = isLeft ? prev : next;
            const icon = isLeft ? arrowConfig.leftIcon : arrowConfig.rightIcon;
            
            const isDisabled = !loop && (
                (isLeft && currentIndex === 0) || 
                (!isLeft && currentIndex === data.length - 1)
            );
            
            return (
                <Pressable
                    onPress={() => handlePress(true)}
                    disabled={isDisabled}
                    style={({ pressed }) => [
                        styles.arrowButton,
                        {
                            width: arrowConfig.size,
                            height: arrowConfig.size,
                            borderRadius: arrowConfig.borderRadius,
                            backgroundColor: arrowConfig.backgroundColor,
                            opacity: isDisabled ? 0.3 : 1,
                        },
                        pressed && styles.arrowButtonPressed,
                    ]}
                >
                    {icon || (
                        <Text style={[styles.arrowIcon, { color: arrowConfig.color }]}>
                            {isLeft ? '‹' : '›'}
                        </Text>
                    )}
                </Pressable>
            );
        };
        
        if (arrowConfig.position === 'bottom') {
            return (
                <View style={styles.arrowBottomContainer}>
                    <ArrowButton direction="left" />
                    <ArrowButton direction="right" />
                </View>
            );
        }
        
        const isOutside = arrowConfig.position === 'outside';
        
        return (
            <>
                <View style={[
                    styles.arrowContainer,
                    styles.arrowLeft,
                    isOutside && styles.arrowLeftOutside,
                ]}>
                    <ArrowButton direction="left" />
                </View>
                <View style={[
                    styles.arrowContainer,
                    styles.arrowRight,
                    isOutside && styles.arrowRightOutside,
                ]}>
                    <ArrowButton direction="right" />
                </View>
            </>
        );
    }, [arrowConfig, data.length, loop, currentIndex, prev, next, styles]);

    // 主渲染
    return (
        <View 
            style={[styles.container, { borderRadius }, style]} 
            testID={testID}
        >
            <View style={styles.contentContainer}>
                <ScrollView
                    ref={scrollViewRef}
                    horizontal={!vertical}
                    pagingEnabled
                    showsHorizontalScrollIndicator={false}
                    showsVerticalScrollIndicator={false}
                    scrollEnabled={swipeable}
                    nestedScrollEnabled={true}
                    onScroll={handleScroll}
                    onScrollBeginDrag={handleScrollBegin}
                    onMomentumScrollEnd={handleScrollEnd}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    scrollEventThrottle={16}
                    decelerationRate="fast"
                    style={styles.scrollView}
                    contentContainerStyle={vertical ? styles.scrollContentVertical : styles.scrollContent}
                >
                    {processedData.map((item, index) => renderSlideItem(item, index))}
                </ScrollView>
                
                {renderIndicatorContent()}
                {renderArrows()}
            </View>
            
            {/* 外部标题 */}
            {titlePosition === 'outside' && data[currentIndex] && renderTitle(data[currentIndex], currentIndex)}
            
            {/* 图片预览 Modal */}
            <ImagePreviewModal
                visible={previewVisible}
                images={data}
                initialIndex={previewInitialIndex}
                previewConfig={previewConfig}
                onClose={closePreview}
            />
        </View>
    );
});

DbSwiper.displayName = 'DbSwiper';

export default DbSwiper;
export type { 
    DbSwiperProps, 
    DbSwiperRef, 
    SwiperItem, 
    IndicatorConfig, 
    ArrowConfig, 
    AutoplayConfig, 
    CardModeConfig,
    VideoConfig,
    PreviewConfig,
    MediaType,
    IndicatorType,
    IndicatorPosition,
    SwiperMode,
    ArrowPosition,
} from './DbSwiper.types';
